import * as React from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useTheme, SelectMini, ISelectOption, Box, Icon } from '@salecycle/ui-component-library';
import { Feature, Ruleset } from 'entities/campaign';
import { Selectors as CampaignSelectors } from 'sections/campaigns/state/campaigns';
import { Actions as RulesetActions, Selectors as RulesetSelectors } from 'sections/campaigns/state/rulesets';
import { State } from 'store';
import { Configs } from '../configs';
import AddRule from './AddRule';
import { Channel as ChannelComponent } from './Channel';
import RulesetDropdown from './RulesetDropdown';
import RulesetName from './RulesetName';
import RuleStep from './RuleStep';
import { Channel, EmailChannel, isOnSiteChannel, ValidNonScheduledType } from 'entities/campaign/rulesets/channels';
import { getChannelConfig, getChannelWithPredefinedTemplateId } from '../configs/newChannels';
import { SortableHandle } from 'react-sortable-hoc';
import ProgressBar from './ProgressBar';
import { getProgress, Progress } from '../progress';
import { getStatusColor } from '../Editor';
import { State as CampaignState } from 'entities/lifecycle/lifeCycle';
import { Selectors as SiteSelectors } from 'sections/client-management/services/sites';
import { Selectors as Router } from 'router';
import { uuid } from 'utils';
import { TFunction } from 'react-i18next';
import { IconLabel } from 'components/experimental/IconLabel';

const StyledStepGrid = styled.div<{ campaignState?: CampaignState }>`
  box-shadow: ${({ theme }) => theme.vars.boxShadow.overlayMenu};
  border: 1px solid ${({ theme }) => theme.vars.newColor.lightGS1};
  background-color: ${({ theme }) => theme.vars.newColor.lightGS2};
  width: 36rem;
  position: relative;
  border-top: ${({ campaignState, theme }) =>
    campaignState ? `0.5rem solid ${getStatusColor(campaignState, theme)}` : null};
`;

const StyledHeader = styled.div`
  margin-bottom: 2rem;

  button {
    border-color: transparent;
    background-color: transparent;
  }

  .priority {
    width: 3.5rem;
    height: 3.5rem;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid ${({ theme }) => theme.vars.newColor.darkGS2};
    border-radius: 100%;
    background-color: ${({ theme }) => theme.vars.newColor.white};
  }
  .controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    .group {
      display: grid;
      grid-template-columns: 1fr 1fr;
      align-items: center;
    }
  }
  .meta {
    p {
      margin: 0;
    }
  }
`;

const StyledDragHandle = styled.div`
  display: flex;
  justify-content: center;
  cursor: grab;
`;

const StyledProgressMessages = styled.div`
  background-color: ${({ theme }) => theme.vars.newColor.darkGS1};
  padding: 2rem;
  color: #fff;

  ul {
    padding-left: 2rem;
    li {
      margin-bottom: 1rem;
    }
  }
`;

const StyledVerticalSpacing = styled.div`
  padding-top: 2rem;
`;

interface OwnProps {
  rulesetId: string;
  index: number;
  campaignState: CampaignState;
}

interface StateProps {
  feature: Feature;
  ruleset: Ruleset;
  progress: (translator: TFunction<'campaigns'>) => Progress;
  sessionTime: number;
}

interface DispatchProps {
  addChannel: (rulesetId: string, channel: Channel) => void;
}

export type IProps = OwnProps & StateProps & DispatchProps;

export const StepGrid: React.FC<IProps> = (props): JSX.Element => {
  const { t } = useTranslation('campaigns');
  const theme = useTheme();

  const config = Configs.getRulesetConfig(props.feature, props.ruleset.subFeature, t);
  const { channels } = props.ruleset;
  const sortedChannels = sortChannels(channels);

  function sortChannels(rawChannels: Channel[]): Channel[] {
    if (rawChannels.length <= 1 || isOnSiteChannel(rawChannels[0])) {
      return rawChannels;
    }
    const emailChannels: EmailChannel[] = [];

    rawChannels.forEach(channel => {
      if (channel.type === '.Email') {
        emailChannels.push(channel);
      }
    });
    emailChannels.forEach(channel => {
      if (channel.cycleId === undefined) {
        // eslint-disable-next-line no-console
        console.error('Unable to sort channels. Missing cycleId');
        return rawChannels;
      }
    });
    const sortedEmailChannels: Array<EmailChannel> = emailChannels.sort((n1, n2) => {
      if (n1.delay > n2.delay) {
        return 1;
      }
      if (n1.delay <= n2.delay) {
        return -1;
      }
      return 0;
    });

    const sortedChannels: Channel[] = [];
    sortedEmailChannels.forEach(c => {
      sortedChannels.push(c);
    });
    return sortedChannels;
  }

  const rulesetPriority = props.index + 1;

  const DragHandle = SortableHandle(() => (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
    <StyledDragHandle tabIndex={0} data-testid="DragHandle">
      <Icon icon="grip-horizontal" color={theme.vars.newColor.darkGS2} />
    </StyledDragHandle>
  ));

  const onSelect = (option: ISelectOption<string>): void => {
    const newChannel = getChannelWithPredefinedTemplateId(option.value as ValidNonScheduledType, null, t);
    if (newChannel.type === '.Email') {
      //Do this here not in default to ensure a new uuid for each new channel
      if (newChannel.cycleId === undefined) {
        newChannel.cycleId = uuid();
      }
      newChannel.delay = props.sessionTime;
    }
    props.addChannel(props.rulesetId, newChannel);
  };

  const newChannelOptions = config.availableChannelTypes.map(channelType => {
    const channelConfig = getChannelConfig(channelType, t);

    return { value: channelConfig.defaultValue.type, label: channelConfig.heading };
  });

  const iconLabelColor = theme.vars.newColor.darkGS2;

  return (
    <StyledStepGrid data-testid="Ruleset" campaignState={props.campaignState}>
      <Box padding="2rem" stackItems={true}>
        {props.progress(t).percent < 100 ? (
          <div data-testid="ruleset-progress">
            <IconLabel icon="arrow-down" color={iconLabelColor} text={t('editor.rulesets.guides.progressGuide')} />
            <StyledVerticalSpacing>
              <ProgressBar percent={props.progress(t).percent} />
            </StyledVerticalSpacing>
          </div>
        ) : null}
        <StyledHeader data-testid="RulesetHeader">
          <div className="controls">
            <div className="priority">{rulesetPriority}</div>
            <div className="group">
              <DragHandle />
              <RulesetDropdown rulesetId={props.rulesetId} />
            </div>
          </div>
          <IconLabel icon="arrow-down" color={iconLabelColor} text={t('editor.rulesets.guides.startRuleset')} />
          <div className="meta">
            <RulesetName rulesetId={props.rulesetId} />
            <p>{config.title}</p>
          </div>
          <StyledVerticalSpacing>
            <IconLabel icon="arrow-down" color={iconLabelColor} text={t('editor.rulesets.guides.whoIsAudience')} />
          </StyledVerticalSpacing>
        </StyledHeader>

        <div data-testid="RulesetRules">
          {props.ruleset.rules.map((rule, n) => (
            <RuleStep
              key={`rule${n}`}
              rulesetId={props.rulesetId}
              ruleIndex={n}
              rule={rule}
              rules={props.ruleset.rules}
              campaignState={props.campaignState}
            />
          ))}
        </div>
        <Box margin="0 0 1rem 0">
          <AddRule rulesetId={props.rulesetId} />
        </Box>
        <IconLabel icon="arrow-down" color={iconLabelColor} text={t('editor.rulesets.guides.whatMessageAndWhen')} />
        {config.multipleChannelsAllowed && (
          <Box stackItems={true} padding="10px 0">
            <SelectMini
              ariaLabel={t('editor.rulesets.channels.addNewChannel.label')}
              value={{ value: null, label: t('editor.rulesets.channels.addNewChannel.label') }}
              options={newChannelOptions}
              onChange={onSelect}
            />
          </Box>
        )}
        <StyledVerticalSpacing data-testid="RulesetChannels">
          {sortedChannels.map(
            (channel, idx): JSX.Element => (
              <ChannelComponent
                channel={channel}
                key={`${props.rulesetId}-channel-${idx}`}
                channelIndex={idx}
                rulesetId={props.rulesetId}
                campaignState={props.campaignState}
              />
            ),
          )}
        </StyledVerticalSpacing>
        <IconLabel icon="arrow-right" color={iconLabelColor} text={t('editor.rulesets.guides.ifNoMatchingAudience')} />
      </Box>
      {props.progress(t).messages.length > 0 && (
        <StyledProgressMessages>
          <h5>*Required</h5>
          <ul>
            {props.progress(t).messages.map((message: string, i: number) => (
              <li key={`progress-message-${i}`}>{message}</li>
            ))}
          </ul>
        </StyledProgressMessages>
      )}
    </StyledStepGrid>
  );
};

export const mapStateToProps = (state: State, ownProps: OwnProps): StateProps => {
  const siteId = Router.getSiteId(state);
  const site = SiteSelectors.getUpdatedEntity(state, siteId);
  const ruleset = RulesetSelectors.getUpdatedEntity(state, ownProps.rulesetId);
  const campaign = CampaignSelectors.getEntity(state, ruleset.campaignId);

  if (ruleset.rules[0].type === '.CartAbandonment') {
    ruleset.rules[0].unsubscribeDefined =
      site?.unsubscribeSettings?.unsubscribeOption === 'UnsubscribeURL' ||
      site?.unsubscribeSettings?.unsubscribeOption === 'GlobalUnsubscribe';
  }
  const sessionTime = site?.sessionTime | 0;
  const progress = t => getProgress(state, ownProps.rulesetId, site.sessionTime, t);
  return {
    feature: campaign.type,
    ruleset,
    progress,
    sessionTime,
  };
};

const actions: DispatchProps = {
  addChannel: RulesetActions.addChannel,
};

export default connect(mapStateToProps, actions)(StepGrid);
